// GameField.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import GameFieldLayout from "layouts/GameFieldLayout";
import { getTreeGameComponent } from "../../GamesFolder/TreeGames/treegame";
import { getListGame } from "../../GamesFolder/List/listgame";
import { getStackGame } from "../../GamesFolder/Stack/stackgame";
import { getGraphGame } from "../../GamesFolder/GraphGames/graphgames";
import { getQueueGame } from "../../GamesFolder/Queue/queuegames";
import { getHashGames } from "../../GamesFolder/HashTable/hashgames";
import { getArrayGames } from "../../GamesFolder/Array/arrayGames";
import axiosInstance from "apiHandles/axiosInstance";
import CryptoJS from 'crypto-js';
import { useLanguage } from "context/LanguageProvider";


const subIds = {
    6: "Directed",
    7: "Undirected",
    3: "Binary Tree",
    4: "AVL Tree",
    5: "Heap",
    9: "Singly Linked List",
    10: "Doubly Linked List",
    11: "1D Array",
    12: "2D Array",
    15: "Array-based Stack",
    16: "Linked List Stack",
    17: "Circular Queue",
    18: "Priority Queue",
    19: "Chaining",
    20: "Open Addressing",
    21: "Hash Set",
    22: "Tree Set",
    24: "Dynamic Array",
};

const SECRET_KEY = process.env.REACT_APP_STRIPE_ENCRYPTION_SECRET_KEY



function decryptData(encryptedData) {
    try {
        const key = CryptoJS.SHA256(SECRET_KEY);

        const decrypted = CryptoJS.AES.decrypt(
            encryptedData,
            key,
            {
                mode: CryptoJS.mode.ECB,
                padding: CryptoJS.pad.Pkcs7
            }
        );

        const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
        return JSON.parse(decryptedString);
    } catch (error) {
        console.error("Decryption failed:", error);
        return null;
    }
}

const GameField = () => {
    const { structure, gameId } = useParams();
    const navigate = useNavigate();

    const [subId, setSubId] = useState(null);
    const [selectedStage, setSelectedStage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const { selLanguage, toggleLanguage, isLading } = useLanguage();


    useEffect(() => {
        const fetchGameData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const selectedStageId = localStorage.getItem("selectedStageId");

                if (!selectedStageId) {
                    setError("❌ No stage ID found");
                    setIsLoading(false);
                    return;
                }

                const stageResponse = await axiosInstance.get(`/api/stage/${selectedStageId}/games/`);
                const decryptedData = decryptData(stageResponse.data.data);

                const stage = decryptedData;
                localStorage.setItem("selectedStage", JSON.stringify(stage));

                if (!stage) {
                    setError(selLanguage ? "Δεν βρέθηκαν δεδομένα για το stage" : "No data were found for this stage");
                    setIsLoading(false);
                    return;
                }

                setSelectedStage(stage);

                const subCategoryId = stage.parent_sub_structure;
                setSubId(subCategoryId);

            } catch (error) {
                console.error("❌ Error fetching game data:", error);
                if (error.response?.status === 401) {
                    alert(selLanguage ? "Πρέπει να κάνεις login ξανά" : "You need to login again");
                    localStorage.removeItem("access");
                    navigate("/login");
                } else {
                    setError(selLanguage ? "Σφάλμα κατά τη φόρτωση του παιχνιδιού: " + error.message : "Error loading the game: " + error.message);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchGameData();
    }, [navigate]);

    const renderGame = () => {
        // Αν φορτώνει
        if (isLoading) {
            return (
                <div style={{
                    color: "white",
                    textAlign: "center",
                    padding: "2rem",
                    fontSize: "1.2rem"
                }}>
                    Loading game...
                </div>
            );
        }

        // Αν υπάρχει error
        if (error) {
            return (
                <div style={{
                    color: "white",
                    textAlign: "center",
                    padding: "2rem"
                }}>
                    <p style={{ color: "#ff6b6b" }}>{error}</p>
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            marginTop: "1rem",
                            padding: "0.5rem 1rem",
                            background: "#3b82f6",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer"
                        }}
                    >
                        Go Back
                    </button>
                </div>
            );
        }

        // Αν λείπουν δεδομένα
        if (!subId || !selectedStage) {
            return (
                <div style={{
                    color: "white",
                    textAlign: "center",
                    padding: "2rem"
                }}>
                    <p>{selLanguage ? "Λείπουν δεδομένα από τη μνήμη." : "Some data are missing."}</p>
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            marginTop: "1rem",
                            padding: "0.5rem 1rem",
                            background: "#3b82f6",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer"
                        }}
                    >
                        Go Back
                    </button>
                </div>
            );
        }

        const subCategoryName = subIds[subId];
        console.log("🎮 Rendering game for:", subCategoryName, "SubID:", subId);

        // Graph Games
        if (subId === 6 || subId === 7) {
            return getGraphGame(subCategoryName, selectedStage);
        }

        // Tree Games
        if (subId === 3 || subId === 4 || subId === 5) {
            return getTreeGameComponent(subCategoryName, selectedStage);
        }

        // List Games
        if (subId === 9 || subId === 10) {
            return getListGame(subCategoryName, selectedStage);
        }

        // Array Games
        if (subId === 11 || subId === 12 || subId === 24) {
            return getArrayGames(subCategoryName, selectedStage);
        }

        // Stack Games
        if (subId === 15 || subId === 16) {
            return getStackGame(subCategoryName, selectedStage);
        }

        // Queue Games
        if (subId === 17 || subId === 18) {
            return getQueueGame(subCategoryName, selectedStage);
        }

        // Hash Games
        if (subId === 19 || subId === 20 || subId === 21 || subId === 22) {
            return getHashGames(subCategoryName, selectedStage);
        }

        // Default case
        return (
            <p style={{ color: "white", textAlign: "center", padding: "2rem" }}>
                Υποδομή "{subCategoryName}" (ID: {subId}) δεν υποστηρίζεται ακόμη.
            </p>
        );
    };

    return (
        <GameFieldLayout title={`Game ${gameId} - ${structure}`}>
            {renderGame()}
        </GameFieldLayout>
    );
};

export default GameField;